import { useMemo, useState } from "react";
import moment from "moment";
import { Select } from "../shared/Select";
import { MarketDropdown } from "../Dropdown/MarketDropdown";
import { useMMTradesAllAssets } from "../../hooks/api/mm/useMMTradesAllAssets";
import {
  mergeLighterFundingData,
  useFundingData,
} from "../../hooks/api/funding/useFundingData";
import { usePerpDataSummary } from "../../hooks/api/mm/usePerpDataSummary";
import { processPnL } from "../../utils/pnl";
import { PnLTable } from "./PnLTable";
import { FundingTable } from "./FundingTable";
import { GeneralTable } from "./GeneralTable";
import {
  FilterGroup,
  FiltersWrapper,
  HeaderRow,
  PageTitle,
  TableContainer,
  PnLWrapper,
  ContentContainer,
  StatsCardsWrapper,
  StatCard,
  StatCardLabel,
  StatCardValue,
  FilterLabel,
  StyledCheckbox,
  TabsWrapper,
  Tab,
  DateInput,
} from "./style";
import { formatAmount } from "../../utils/strings";
import { getValueColor } from "./utils";

type TimePeriod = "24h" | "7d" | "30d";
type TabType = "general" | "pnl" | "funding";

const TIME_PERIODS: { label: string; value: TimePeriod }[] = [
  { label: "24 Hours", value: "24h" },
  { label: "7 Days", value: "7d" },
  { label: "30 Days", value: "30d" },
];

const getTimeRange = (period: TimePeriod): { start: number; end: number } => {
  const now = Date.now();
  let start: number;

  switch (period) {
    case "24h":
      start = now - 24 * 60 * 60 * 1000;
      break;
    case "7d":
      start = now - 7 * 24 * 60 * 60 * 1000;
      break;
    case "30d":
      start = now - 30 * 24 * 60 * 60 * 1000;
      break;
    default:
      start = now - 24 * 60 * 60 * 1000;
  }

  return { start, end: now };
};

export function PnL() {
  const [activeTab, setActiveTab] = useState<TabType>("general");
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>("24h");
  const [showAllTrades, setShowAllTrades] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState<string>("");

  // Funding data filters
  const [fundingStartDate, setFundingStartDate] = useState<string>(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [fundingEndDate, setFundingEndDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [selectedFundingPairs, setSelectedFundingPairs] = useState<string[]>([]);

  const timeRange = useMemo(() => getTimeRange(selectedPeriod), [selectedPeriod]);

  const { data: tradesData, isLoading } = useMMTradesAllAssets(
    timeRange.start.toString(),
    timeRange.end.toString(), // Ensure end_time is always provided
    undefined, // All exchanges
    showAllTrades
  );

  // Filter trades by asset if selected and only include perpetual trades
  const filteredTradesData = useMemo(() => {
    if (!tradesData) {
      return tradesData;
    }

    // eslint-disable-next-line no-console
    console.log("Total tradesData length:", tradesData.length);

    // First filter for perpetual trades only (strategy = perp)
    let perpTrades = tradesData.filter((trade) => trade.instrument_name.toUpperCase().includes("-PERP"));

    // eslint-disable-next-line no-console
    console.log("After PERP filter length:", perpTrades.length);

    // Then filter by asset if selected
    if (selectedAsset) {
      perpTrades = perpTrades.filter((trade) => {
        const asset = trade.instrument_name.split("-")[0];
        return asset.toUpperCase() === selectedAsset.toUpperCase();
      });
      // eslint-disable-next-line no-console
      console.log(`After ${selectedAsset} filter length:`, perpTrades.length);
    }

    return perpTrades;
  }, [tradesData, selectedAsset]);

  const pnlData = useMemo(() => {
    if (!filteredTradesData || filteredTradesData.length === 0) {
      return null;
    }

    return processPnL(filteredTradesData, timeRange.start, timeRange.end, "MM");
  }, [filteredTradesData, timeRange.start, timeRange.end]);

  // Funding data - aggregate by asset only (sum over dates)
  // Use the same time range as PnL for General tab, or use date pickers for Funding tab
  const fundingDateRange = useMemo(() => {
    if (activeTab === "general") {
      // For General tab, use the same time period as PnL
      return {
        startDate: moment(timeRange.start).format("YYYY-MM-DD"),
        endDate: moment(timeRange.end).format("YYYY-MM-DD"),
      };
    }
    return {
      startDate: fundingStartDate,
      endDate: fundingEndDate,
    };
  }, [activeTab, timeRange, fundingStartDate, fundingEndDate]);

  // Determine funding pairs based on active tab and selected asset
  const fundingPairs = useMemo(() => {
    if (activeTab === "funding") {
      return selectedFundingPairs.length > 0 ? selectedFundingPairs : undefined;
    }
    // For General tab, filter by selected asset if one is selected
    if (activeTab === "general" && selectedAsset) {
      return [`${selectedAsset}-PERP`];
    }
    return undefined;
  }, [activeTab, selectedAsset, selectedFundingPairs]);

  const { data: fundingData, isLoading: isFundingLoading } = useFundingData({
    groupByAssetOnly: true,
    startDate: fundingDateRange.startDate,
    endDate: fundingDateRange.endDate,
    pairs: fundingPairs,
  });

  const fundingSummaryRange = useMemo(() => {
    const start = moment
      .utc(fundingDateRange.startDate)
      .startOf("day")
      .valueOf();
    const requestedEnd = moment
      .utc(fundingDateRange.endDate)
      .endOf("day")
      .valueOf();
    return { start, end: Math.min(requestedEnd, Date.now()) };
  }, [fundingDateRange]);

  const fundingSummaryAssets = useMemo(
    () => fundingPairs?.map((pair) => pair.replace(/-PERP$/i, "")),
    [fundingPairs]
  );

  const { data: perpDataSummary, isLoading: isPerpDataSummaryLoading } =
    usePerpDataSummary({
      startMs: fundingSummaryRange.start,
      endMs: fundingSummaryRange.end,
      assets: fundingSummaryAssets,
      refreshInterval: 0,
      enabled:
        (activeTab === "general" || activeTab === "funding") &&
        fundingSummaryRange.start < fundingSummaryRange.end,
    });

  const effectiveFundingData = useMemo(
    () => mergeLighterFundingData(fundingData, perpDataSummary),
    [fundingData, perpDataSummary]
  );
  const isEffectiveFundingLoading =
    isFundingLoading || isPerpDataSummaryLoading;

  const periodOptions = useMemo(
    () =>
      TIME_PERIODS.map(({ label, value }) => ({
        label,
        isActive: selectedPeriod === value,
        onClick: () => setSelectedPeriod(value),
      })),
    [selectedPeriod]
  );

  // Calculate aggregated stats
  const aggregatedStats = useMemo(() => {
    if (!pnlData || pnlData.length === 0) {
      return {
        totalPnL: 0,
        totalNetPnL: 0,
        totalBuyVolume: 0,
        totalSellVolume: 0,
        totalBuyAmount: 0,
        totalSellAmount: 0,
        avgBuyPrice: 0,
        avgSellPrice: 0,
      };
    }

    const totalPnL = pnlData.reduce((sum, row) => sum + row.PnL, 0);
    const totalNetPnL = pnlData.reduce((sum, row) => sum + row.NetPnL, 0);
    const totalBuyVolume = pnlData.reduce((sum, row) => sum + row.TotalBuyVolume, 0);
    const totalSellVolume = pnlData.reduce((sum, row) => sum + row.TotalSellVolume, 0);
    const totalBuyAmount = pnlData.reduce((sum, row) => sum + row.TotalBuyAmount, 0);
    const totalSellAmount = pnlData.reduce((sum, row) => sum + row.TotalSellAmount, 0);
    const avgBuyPrice = totalBuyAmount > 0 ? totalBuyVolume / totalBuyAmount : 0;
    const avgSellPrice = totalSellAmount > 0 ? totalSellVolume / totalSellAmount : 0;

    return {
      totalPnL,
      totalNetPnL,
      totalBuyVolume,
      totalSellVolume,
      totalBuyAmount,
      totalSellAmount,
      avgBuyPrice,
      avgSellPrice,
    };
  }, [pnlData]);

  // Calculate aggregated funding stats
  const aggregatedFundingStats = useMemo(() => {
    if (!effectiveFundingData || effectiveFundingData.length === 0) {
      return {
        totalAevoFunding: 0,
        totalBinanceFunding: 0,
        totalBybitFunding: 0,
        totalNetFunding: 0,
      };
    }

    const totalAevoFunding = effectiveFundingData.reduce((sum, row) => sum + (row.Aevo_Funding || 0), 0);
    const totalBinanceFunding = effectiveFundingData.reduce((sum, row) => sum + (row.Binance_Funding || 0), 0);
    const totalBybitFunding = effectiveFundingData.reduce((sum, row) => sum + (row.Bybit_Funding || 0), 0);
    const totalNetFunding = effectiveFundingData.reduce((sum, row) => sum + (row.Net_Funding || 0), 0);

    return {
      totalAevoFunding,
      totalBinanceFunding,
      totalBybitFunding,
      totalNetFunding,
    };
  }, [effectiveFundingData]);

  // Calculate aggregated general stats (Net PnL + Net Funding)
  const aggregatedGeneralStats = useMemo(() => {
    if (!pnlData || !effectiveFundingData) {
      return {
        totalNetPnL: 0,
        totalNetFunding: 0,
        totalNetTotal: 0,
      };
    }

    const totalNetPnL = pnlData.reduce((sum, row) => sum + row.NetPnL, 0);
    const totalNetFunding = effectiveFundingData.reduce((sum, row) => sum + (row.Net_Funding || 0), 0);
    const totalNetTotal = totalNetPnL + totalNetFunding;

    return {
      totalNetPnL,
      totalNetFunding,
      totalNetTotal,
    };
  }, [pnlData, effectiveFundingData]);

  return (
    <PnLWrapper>
      <ContentContainer>
        <HeaderRow>
          <PageTitle>
            {activeTab === "general" && "General Analysis"}
            {activeTab === "pnl" && "PnL Analysis"}
            {activeTab === "funding" && "Funding Data"}
          </PageTitle>
        </HeaderRow>
        <TabsWrapper>
          <Tab isActive={activeTab === "general"} onClick={() => setActiveTab("general")}>
            General
          </Tab>
          <Tab isActive={activeTab === "pnl"} onClick={() => setActiveTab("pnl")}>
            PnL Analysis
          </Tab>
          <Tab isActive={activeTab === "funding"} onClick={() => setActiveTab("funding")}>
            Funding Data
          </Tab>
        </TabsWrapper>
        {activeTab === "general" && (
          <>
            <FiltersWrapper>
              <FilterGroup>
                <span>Time Period</span>
                <Select options={periodOptions} />
              </FilterGroup>
              <FilterGroup>
                <span>Asset</span>
                <MarketDropdown onClick={(value) => setSelectedAsset(value || "")} overridePlaceholder="All Assets" />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>
                  <StyledCheckbox
                    type="checkbox"
                    checked={showAllTrades}
                    onChange={(e) => setShowAllTrades(e.target.checked)}
                  />
                  Show All Trades
                </FilterLabel>
              </FilterGroup>
            </FiltersWrapper>
            <StatsCardsWrapper isAllAssets>
              <StatCard>
                <StatCardLabel>Total Net (PnL + Funding)</StatCardLabel>
                <StatCardValue
                  style={{
                    color: getValueColor(aggregatedGeneralStats.totalNetTotal),
                  }}
                >
                  {aggregatedGeneralStats.totalNetTotal > 0 ? "+" : ""}
                  {formatAmount(aggregatedGeneralStats.totalNetTotal)}
                </StatCardValue>
              </StatCard>
              <StatCard>
                <StatCardLabel>Total Net PnL</StatCardLabel>
                <StatCardValue
                  style={{
                    color: getValueColor(aggregatedGeneralStats.totalNetPnL),
                  }}
                >
                  {aggregatedGeneralStats.totalNetPnL > 0 ? "+" : ""}
                  {formatAmount(aggregatedGeneralStats.totalNetPnL)}
                </StatCardValue>
              </StatCard>
              <StatCard>
                <StatCardLabel>Total Net Funding</StatCardLabel>
                <StatCardValue
                  style={{
                    color: getValueColor(aggregatedGeneralStats.totalNetFunding),
                  }}
                >
                  {aggregatedGeneralStats.totalNetFunding > 0 ? "+" : ""}
                  {formatAmount(aggregatedGeneralStats.totalNetFunding)}
                </StatCardValue>
              </StatCard>
            </StatsCardsWrapper>
            <TableContainer>
              <GeneralTable
                pnlData={pnlData || []}
                fundingData={effectiveFundingData || []}
                isLoadingPnL={isLoading}
                isLoadingFunding={isEffectiveFundingLoading}
              />
            </TableContainer>
          </>
        )}
        {activeTab === "pnl" && (
          <>
            <FiltersWrapper>
              <FilterGroup>
                <span>Time Period</span>
                <Select options={periodOptions} />
              </FilterGroup>
              <FilterGroup>
                <span>Asset</span>
                <MarketDropdown onClick={(value) => setSelectedAsset(value || "")} overridePlaceholder="All Assets" />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>
                  <StyledCheckbox
                    type="checkbox"
                    checked={showAllTrades}
                    onChange={(e) => setShowAllTrades(e.target.checked)}
                  />
                  Show All Trades
                </FilterLabel>
              </FilterGroup>
            </FiltersWrapper>
            <StatsCardsWrapper isAllAssets={!selectedAsset}>
              <StatCard>
                <StatCardLabel>Total PnL (Before Fees)</StatCardLabel>
                <StatCardValue
                  style={{
                    color: getValueColor(aggregatedStats.totalPnL),
                  }}
                >
                  {aggregatedStats.totalPnL > 0 ? "+" : ""}
                  {formatAmount(aggregatedStats.totalPnL)}
                </StatCardValue>
              </StatCard>
              <StatCard>
                <StatCardLabel>Total PnL (After Fees)</StatCardLabel>
                <StatCardValue
                  style={{
                    color: getValueColor(aggregatedStats.totalNetPnL),
                  }}
                >
                  {aggregatedStats.totalNetPnL > 0 ? "+" : ""}
                  {formatAmount(aggregatedStats.totalNetPnL)}
                </StatCardValue>
              </StatCard>
              {selectedAsset && (
                <>
                  <StatCard>
                    <StatCardLabel>Buy Volume</StatCardLabel>
                    <StatCardValue>{formatAmount(aggregatedStats.totalBuyVolume)}</StatCardValue>
                  </StatCard>
                  <StatCard>
                    <StatCardLabel>Sell Volume</StatCardLabel>
                    <StatCardValue>{formatAmount(aggregatedStats.totalSellVolume)}</StatCardValue>
                  </StatCard>
                  <StatCard>
                    <StatCardLabel>Buy Avg Price</StatCardLabel>
                    <StatCardValue>{formatAmount(aggregatedStats.avgBuyPrice)}</StatCardValue>
                  </StatCard>
                  <StatCard>
                    <StatCardLabel>Sell Avg Price</StatCardLabel>
                    <StatCardValue>{formatAmount(aggregatedStats.avgSellPrice)}</StatCardValue>
                  </StatCard>
                </>
              )}
            </StatsCardsWrapper>
            <TableContainer>
              <PnLTable pnlData={pnlData || []} isLoading={isLoading} />
            </TableContainer>
          </>
        )}
        {activeTab === "funding" && (
          <>
            <FiltersWrapper>
              <FilterGroup>
                <span>Start Date</span>
                <DateInput
                  type="date"
                  value={fundingStartDate}
                  onChange={(e) => setFundingStartDate(e.target.value)}
                />
              </FilterGroup>
              <FilterGroup>
                <span>End Date</span>
                <DateInput
                  type="date"
                  value={fundingEndDate}
                  onChange={(e) => setFundingEndDate(e.target.value)}
                />
              </FilterGroup>
              <FilterGroup>
                <span>Asset</span>
                <MarketDropdown
                  onClick={(value) => {
                    if (value) {
                      // Format as ASSET-PERP for perpetual pairs
                      const pair = `${value}-PERP`;
                      setSelectedFundingPairs([pair]);
                    } else {
                      setSelectedFundingPairs([]);
                    }
                  }}
                  overridePlaceholder="All Assets"
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>
                  <StyledCheckbox
                    type="checkbox"
                    checked
                    disabled
                    style={{ cursor: "not-allowed" }}
                  />
                  Aggregate by Asset (Sum over Dates)
                </FilterLabel>
              </FilterGroup>
            </FiltersWrapper>
            <StatsCardsWrapper isAllAssets>
              <StatCard>
                <StatCardLabel>Total Net Funding</StatCardLabel>
                <StatCardValue
                  style={{
                    color: getValueColor(aggregatedFundingStats.totalNetFunding),
                  }}
                >
                  {aggregatedFundingStats.totalNetFunding > 0 ? "+" : ""}
                  {formatAmount(aggregatedFundingStats.totalNetFunding)}
                </StatCardValue>
              </StatCard>
              <StatCard>
                <StatCardLabel>Total Aevo Funding</StatCardLabel>
                <StatCardValue
                  style={{
                    color: getValueColor(aggregatedFundingStats.totalAevoFunding),
                  }}
                >
                  {aggregatedFundingStats.totalAevoFunding > 0 ? "+" : ""}
                  {formatAmount(aggregatedFundingStats.totalAevoFunding)}
                </StatCardValue>
              </StatCard>
              <StatCard>
                <StatCardLabel>Total Binance Funding</StatCardLabel>
                <StatCardValue
                  style={{
                    color: getValueColor(aggregatedFundingStats.totalBinanceFunding),
                  }}
                >
                  {aggregatedFundingStats.totalBinanceFunding > 0 ? "+" : ""}
                  {formatAmount(aggregatedFundingStats.totalBinanceFunding)}
                </StatCardValue>
              </StatCard>
              <StatCard>
                <StatCardLabel>Total Bybit Funding</StatCardLabel>
                <StatCardValue
                  style={{
                    color: getValueColor(aggregatedFundingStats.totalBybitFunding),
                  }}
                >
                  {aggregatedFundingStats.totalBybitFunding > 0 ? "+" : ""}
                  {formatAmount(aggregatedFundingStats.totalBybitFunding)}
                </StatCardValue>
              </StatCard>
            </StatsCardsWrapper>
            <TableContainer>
              <FundingTable fundingData={effectiveFundingData || []} isLoading={isEffectiveFundingLoading} />
            </TableContainer>
          </>
        )}
      </ContentContainer>
    </PnLWrapper>
  );
}
